import { root, getServiceVersion, IAppConfig } from '@estee/elc-universal-utils';
import { getDecorators } from '@estee/elc-service';
import { Container } from 'hypo-container';
import { ProductRatingViewController } from '~controllers/ProductRatingViewController';
import { ProductReviewsViewController } from '~controllers/ProductReviewsViewController';
import { ReviewDetailsViewController } from '~controllers/ReviewDetailsViewController';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { ProductRepository } from '~repositories/ProductRepository';
import { Config } from '~domain/entities/Config';
import { RatingsAndReviewsApiSdk } from '~api/RatingsAndReviewsApiSdk';
import { ServiceBusController } from './ServiceBusController';
import { RatingsReviewsService } from './RatingsReviewsService';
import { ConfigStore } from './ConfigStore';
import { RatingsRepository } from '~repositories/RatingsRepository';
import serviceNames from '~setup/RatingsReviewsService';
import { RatingsReviewsServiceStartedUseCase } from '~use-cases/RatingsReviewsServiceStartedUseCase';

export const diContainer = new Container();

export const setConfig = (configJSON: IAppConfig) => {
    diContainer.config = configJSON;
};

async function serviceInitializer(config: IAppConfig) {
    setConfig(config);

    diContainer.get(serviceNames.serviceBusController);
    const useCase = new RatingsReviewsServiceStartedUseCase(['ratingsReviewsConfig', 'brand', 'i18n']);
    await useCase.execute();
}

diContainer
    .register(serviceNames.configStore, (c) => new ConfigStore(c.config))
    .register(serviceNames.config, (c) => new Config())
    .register(serviceNames.configRepository, () => new ConfigRepository())
    .register(
        serviceNames.serviceBusController,
        (c) => new ServiceBusController(c.get(serviceNames.ratingsRepository))
    )
    .register(
        serviceNames.ratingsReviewsService,
        () => new RatingsReviewsService(serviceInitializer)
    )
    .register(
        serviceNames.productRatingViewController,
        diContainer.factory(
            (c) =>
                new ProductRatingViewController(
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.ratingsRepository)
                )
        )
    )
    .register(
        serviceNames.productReviewsViewController,
        diContainer.factory(
            (c) =>
                new ProductReviewsViewController(
                    c.get(serviceNames.config),
                    c.get(serviceNames.productRepository)
                )
        )
    )
    .register(serviceNames.ratingsReviewsApiSdk, (c) => {
        const config = {
            httpTransport: c.get(serviceNames.httpTransport),
            configStore: c.get(serviceNames.configStore)
        };
        const reviewSnippetServiceName = serviceNames.ratingsReviewsApiSdk;
        const reviewSnippetServiceVersion = getServiceVersion(reviewSnippetServiceName);

        return new RatingsAndReviewsApiSdk(
            config,
            reviewSnippetServiceName,
            reviewSnippetServiceVersion
        );
    })
    .register(serviceNames.httpTransport, (c) => {
        return root.HttpTransport;
    })
    .register(
        serviceNames.productRepository,
        (c) => new ProductRepository(c.get(serviceNames.config))
    )
    .register(serviceNames.ratingsRepository, (c) => new RatingsRepository())
    .register(
        serviceNames.reviewDetailsViewController,
        (c) => new ReviewDetailsViewController(c.get(serviceNames.configStore))
    );

const { lazyInject } = getDecorators(diContainer);

export { lazyInject };
